import StepTypes from 'root/activities/hub/data/StepTypes';
import Env from 'lib/Env';
import { getTimestamp } from 'root/lib/DatetimeManager';
import {
  adjustLevels,
  findChapterName,
  getLevelsMasteredFromSkillLevel,
  filterContentSetsForUniqueEReader,
  isGameIncompleteAndDifferentProduct,
} from 'lib/utilityFunctions';
import { WORKSHOP_BENCHMARK_IDS } from 'a/hub/data/constants';
import WorkshopHardcodedGamesWBB from 'a/hub/data/WorksopHardcodedGamesWBB.json';
import WorkshopQuestsNormal from 'a/hub/data/WorkshopQuests.json';

/*
*  function to adjust Quest Steps based on content service API
*/
export default function adjustQuestsContent(state, contentSets, product, skillLevels) {
  // skip adaptivity if no active quests
  if (state.currentActiveQuestId === 0) {
    return state;
  }
  const questContent = filterContentSetsForUniqueEReader(contentSets).map(
    content => ({ ...content.Content[0], skillId: content.Skill }),
  );

  const questStepsFromSkills = questContent.map(
    step => ({
      game: step.GameId,
      levels: step.Levels,
      levelsMastered: getLevelsMasteredFromSkillLevel(step.skillId, step.Levels, skillLevels),
      type: step.GameId.includes('ereader' || 'eReader') ? StepTypes.EREADER : StepTypes.GAME,
      friendlyName: step.GameId.includes('ereader' || 'eReader') ? step.GameName : '',
      skillId: step.skillId,
      source: 'adaptivity',
      product,
    }),
  );

  // put in-progress skills first
  questStepsFromSkills.sort((stepA, stepB) => {
    const stepAProgress = stepA.levelsMastered.length / stepA.levels.length;
    const stepBProgress = stepB.levelsMastered.length / stepB.levels.length;

    if (stepAProgress > stepBProgress) {
      return -1;
    } if (stepAProgress < stepBProgress) {
      return 1;
    }
    return 0;
  });

  if (!Env.isReleaseEnv) {
    // request from QA to easily see what comes from service
    console.log(`Games provided by adaptivity service:  ${JSON.stringify(questStepsFromSkills)}`);
  }

  function resetEreaderBook(eReaderBook) {
    const storedEreaderBookData = state.gamesData[eReaderBook];
    if (storedEreaderBookData.GameData) {
      const { gameDetails } = storedEreaderBookData.GameData;
      if (gameDetails) {
        storedEreaderBookData.GameData.gameDetails.status = '';
        storedEreaderBookData.GameData.gameDetails.attempts = 0;
      }
    }
  }

  const questsStateObject = { ...state };
  const questStepsFromReducer = questsStateObject.quests[`quest${state.currentActiveQuestId}`].steps;
  let stepIndex = 0;
  let skillsIndex = 0;
  const cachedGames = [];
  questStepsFromReducer.forEach((step) => {
    // assume any step without a product field came from READING
    // if step is completed, do not replace, if product is not current product, swap out game with
    // update game step only if no progress with game yet, or if step is hardcoded and incomplete
    if (
      (
        isGameIncompleteAndDifferentProduct(step, product)
        || (step.source !== 'adaptivity' && step.levelsMastered.length < step.levels.length)
      )
      && questStepsFromSkills.length > stepIndex
      && questStepsFromSkills.length > skillsIndex
    ) {
      const { game, type, friendlyName, skillId, levels, levelsMastered } = questStepsFromSkills[skillsIndex];
      const adaptivityStepObject = {
        game,
        type,
        friendlyName,
        skillId,
        levels: adjustLevels(levels),
        levelsMastered,
        source: 'adaptivity',
        product,
      };
      questStepsFromReducer[stepIndex] = adaptivityStepObject;
      if (type === StepTypes.EREADER) {
        questStepsFromReducer[stepIndex].chapter = findChapterName(game, questStepsFromReducer[stepIndex].levels[0]);
        // find eReader books we have to clean/reset
        resetEreaderBook(game);
      }
      cachedGames.push(adaptivityStepObject);
      skillsIndex++;
    } else {
      cachedGames.push(step);
      if (questStepsFromSkills.length > skillsIndex && step.skillId === questStepsFromSkills[skillsIndex].skillId) {
        // Prevent adding a game level from adaptivity if it is already in the quest
        skillsIndex++;
      }
    }
    stepIndex++;
  });

  questsStateObject.updated = getTimestamp();

  let sideQuestCorrection = 0;
  if (state.sideQuest.showSideQuestAvailability || state.sideQuest.showChallengeIconOnMap) {
    sideQuestCorrection = 0.5;
  }

  questsStateObject.contentServiceQuestId = questsStateObject.currentActiveQuestId - sideQuestCorrection;

  let gameIndex = 0;
  const storedSideQuestData = questsStateObject.sideQuest.games;
  // clear games saved in previous sessions
  questsStateObject.sideQuest.games = [];
  const sideQuestGames = questsStateObject.sideQuest.games;
  // we need only games in side quest, no ereader at all
  const gamesForSideQuestFromSkills = questStepsFromSkills.filter(game => game.type === StepTypes.GAME);
  gamesForSideQuestFromSkills.forEach((game) => {
    // side quests can have up to 3 games only
    if (sideQuestGames.length < 3) {
      // add unique game only
      if (sideQuestGames.filter(uniqueGame => uniqueGame.game === game.game).length === 0) {
        sideQuestGames[gameIndex] = {
          type: StepTypes.GAME,
          game: game.game,
          levels: adjustLevels(game.levels),
          skillId: game.skillId,
          levelsMastered: [],
          friendlyName: '',
          source: 'adaptivity',
          product,
        };
        gameIndex++;
      }
    }
  });

  sideQuestGames.forEach((game, index) => {
    if (questsStateObject.sideQuest.selectedGame === game.game) {
      sideQuestGames[index].levelsMastered = storedSideQuestData
        .find(storedGame => storedGame.game === game.game).levelsMastered || [];
    }
  });
  return [{ ...questsStateObject }, cachedGames];
}

export function updateWorkshopGames(state, gameContent) {
  const { currentQuestId, benchmark, workshop } = state;

  // Skip adaptivity if end of quests
  if (currentQuestId === 0) {
    return state;
  }

  const questKey = `quest${currentQuestId}`;
  const currentQuest = workshop[questKey];
  const isWellBelow = benchmark === WORKSHOP_BENCHMARK_IDS.WELL_BELOW;
  const gamesCount = isWellBelow ? 6 : 3;

  // Get hardcoded games for this quest as fallback
  const hardcodedGames = isWellBelow
    ? WorkshopHardcodedGamesWBB[questKey] || []
    : WorkshopQuestsNormal.workshop[questKey]?.games || [];

  // Transform content sets to workshop games, filtering out ereader books
  const workshopGamesFromSkills = gameContent
    .map(content => ({ ...content.Content[0], skillId: content.Skill }))
    .filter(step => !step.GameId.includes('ereader' || 'eReader'))
    .map(step => ({
      game: step.GameId,
      levels: step.Levels,
      levelsMastered: [],
      type: StepTypes.GAME,
      friendlyName: '',
      skillId: step.skillId,
      source: 'adaptivity',
    }));

  // Build games array: use existing games, fallback to hardcoded (never create placeholders)
  const reducerGames = currentQuest.games;
  let skillGamesIndex = 0;

  const updatedGames = Array.from({ length: gamesCount }, (_, index) => {
    // Use existing game from state, or fallback to hardcoded game
    const currentGame = reducerGames[index] || hardcodedGames[index];
    const isPlaceholder = currentGame?.source === 'placeholder';
    const isNotStarted = currentGame?.levelsMastered?.length === 0 && !currentGame?.adminInitiated;

    // Replace placeholders or unstarted games with adaptivity games if available
    if ((isPlaceholder || isNotStarted) && skillGamesIndex < workshopGamesFromSkills.length) {
      const adaptivityGame = workshopGamesFromSkills[skillGamesIndex];
      skillGamesIndex += 1;
      return {
        ...adaptivityGame,
        levels: adjustLevels(adaptivityGame.levels),
      };
    }

    return currentGame;
  });

  return {
    ...state,
    workshop: {
      ...workshop,
      [questKey]: {
        ...currentQuest,
        games: updatedGames,
      },
    },
  };
}
